import { Avatar } from '../ui';
import PremiumName from '../ui/PremiumName';
import { formatPostDate, formatFullDate } from '../../utils/dateFormatter';
import { useUserData } from '../../hooks/useUserData';

/**
 * Componente de lista de comentarios
 */
const CommentItem = ({ comment, currentUserId, onDelete }) => {
  const { userData } = useUserData(comment.userId);
  const isOwner = currentUserId === comment.userId;

  return (
    <div className="flex gap-3 group">
      <Avatar
        src={userData?.photoURL || comment.userPhotoURL}
        alt={userData?.displayName || comment.userName}
        fallbackText={userData?.displayName || comment.userName}
        size="sm"
      />
      
      <div className="flex-1 min-w-0">
        <div className="bg-gray-50 dark:bg-slate-800 rounded-2xl px-3 py-2">
          <div className="flex items-center gap-2 mb-1">
            <PremiumName
              name={userData?.displayName || comment.userName || 'Usuario'}
              isPremium={userData?.isPremium}
              premiumColor={userData?.premiumNameColor}
              nameClassName="text-sm font-semibold text-gray-900 dark:text-white"
            />
            <span 
              className="text-xs text-gray-400 dark:text-gray-500"
              title={formatFullDate(comment.createdAt)}
            >
              {formatPostDate(comment.createdAt)}
            </span>
          </div>
          <p className="text-sm text-gray-700 dark:text-gray-300 break-words">
            {comment.text}
          </p>
        </div>

        {/* Botón eliminar (solo para el dueño) */}
        {isOwner && (
          <button
            onClick={() => onDelete(comment.id)}
            className="ml-2 mt-1 text-xs text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            Eliminar
          </button>
        )}
      </div>
    </div>
  );
};

const CommentsList = ({ comments, currentUserId, onDeleteComment, loading }) => {
  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="flex gap-3 animate-pulse">
            <div className="w-8 h-8 bg-gray-200 dark:bg-slate-700 rounded-full" />
            <div className="flex-1">
              <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-1/4 mb-2" />
              <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded w-3/4" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (comments.length === 0) {
    return (
      <div className="text-center py-8">
        <svg className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No hay comentarios aún
        </p>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
          Sé el primero en comentar
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {comments.map(comment => (
        <CommentItem
          key={comment.id}
          comment={comment}
          currentUserId={currentUserId}
          onDelete={onDeleteComment}
        />
      ))}
    </div>
  );
};

export default CommentsList;
